import { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSettings } from '@/SettingsContext';
import { useLanguage } from '@/LanguageContext';
import { GridScan } from '../ui/GridScan';

// ====================================================================
//
//   ██████╗ ██████╗  ██████╗      ██╗███████╗ ██████╗████████╗███████╗
//   ██╔══██╗██╔══██╗██╔═══██╗     ██║██╔════╝██╔════╝╚══██╔══╝██╔════╝
//   ██████╔╝██████╔╝██║   ██║     ██║█████╗  ██║        ██║   ███████╗
//   ██╔═══╝ ██╔══██╗██║   ██║██   ██║██╔══╝  ██║        ██║   ╚════██║
//   ██║     ██║  ██║╚██████╔╝╚█████╔╝███████╗╚██████╗   ██║   ███████║
//   ╚═╝     ╚═╝  ╚═╝ ╚═════╝  ╚════╝ ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝
//
//   MASTER PANEL — Tüm görsel ayarlar buradan yönetilir.
//
// ====================================================================

const PROJECTS_SETTINGS = {

  // ─────────────────────────────────────────────
  //  📁  PROJELER — Buradan proje ekle/çıkar/düzenle
  // ─────────────────────────────────────────────
  //  Her proje için:
  //    id:     Benzersiz numara/string
  //    title:  Proje başlığı
  //    author: Alt yazı (ekip, müşteri, yıl vb.)
  //    image:  Görsel yolu — "/media/images/proje.jpg" veya Unsplash URL
  //    tags:   Etiket dizisi — ["UI", "Web Design"]
  //    color:  Hover rengi (hex) — görselin üzerine gelen renk tonu
  //
  projects: [
    {
      id: '1',
      title: 'Atomic Feelings®',
      author: 'Creative Team',
      image: '/media/images/atomicF.jpg',
      tags: ['UI', 'Web Design'],
      color: '#999999',
    },
    {
      id: '2',
      title: 'Atomic Dreams',
      author: 'Design Studio',
      image: '/media/images/dreams.jpg',
      tags: ['Branding', 'Product'],
      color: '#00d4ff',
    },
    {
      id: '3',
      title: 'Dhu-Al Qureyza',
      author: 'Short Film',
      image: '/media/images/dhual.jpg',
      tags: ['Motion', 'Identity'],
      color: '#ff6b6b',
    },
    {
      id: '4',
      title: 'AMARA',
      author: 'Brand Design',
      image: 'https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?w=800&q=80',
      tags: ['Web', 'Branding'],
      color: '#ffd93d',
    },
    {
      id: '5',
      title: 'Plan B®',
      author: 'Digital Agency',
      image: 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?w=800&q=80',
      tags: ['UI/UX', 'Website'],
      color: '#6c5ce7',
    },
    {
      id: '6',
      title: 'WADO',
      author: 'Product Design',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
      tags: ['E-commerce', 'Identity'],
      color: '#00cec9',
    },
    // ↓ Yeni proje eklemek için buraya kopyala:
    // {
    //   id: '7',
    //   title: 'PROJE ADI',
    //   author: 'Müşteri / Ekip',
    //   image: '/media/images/proje7.jpg',
    //   tags: ['Kategori'],
    //   color: '#ffffff',
    // },
  ],

  // ─────────────────────────────────────────────
  //  📐  LAYOUT
  // ─────────────────────────────────────────────
  layout: {
    // Aynı anda kaç proje görünsün (masaüstü)
    // 2 = büyük kartlar | 3 = dengeli | 4 = küçük kartlar
    visibleCount: 3,

    // Kartlar arası boşluk
    cardGap: "gap-6",

    // Kart görsel oranı: "4/3" = yatay | "3/4" = dikey | "1/1" = kare
    cardAspect: "aspect-[3/4]",
  },

  // ─────────────────────────────────────────────
  //  🏷️  BAŞLIK (SEE THE WORK)
  // ─────────────────────────────────────────────
  heading: {
    line1: "SEE THE WORK",
    line2: "OF OUR STUDENTS",

    // Su dolma efekti renkleri
    // fillColor: dolacak renk | outlineColor: boş haldeki kontur rengi
    fillColor: "#ffffff",      // Dolan kısım rengi
    outlineColor: "rgba(255,255,255,0.15)", // Boş kısım rengi
    fillDuration: 1.8,            // Saniye — ne kadar sürede dolsun (1 = hızlı | 3 = yavaş)
    fillDelay: 0.3,            // Görünüme girince dolmaya başlamadan önceki bekleme
  },

  // ─────────────────────────────────────────────
  //  ⬅️➡️  NEON OKLAR
  // ─────────────────────────────────────────────
  arrows: {
    // Neon renk paleti — ok hover'ında bu renklerden rastgele seçilir
    colors: ["#00f0ff", "#bc13fe", "#ff003c", "#ffed00", "#00ff88"],

    // Ok kutusu border ve arkaplan
    borderDefault: "border-black/30 dark:border-white/20",
    borderHover: "border-white/0",   // hover'da border kaybolur, neon glow alır
    bgHover: "bg-black/5 dark:bg-white/5",

    // Glow kuvveti — büyük = daha yayılan ışık
    // "0 0 8px X, 0 0 20px X, 0 0 40px X" → X = neon rengi
    glowLayers: 3,    // 1-3 arası (1 = sade | 3 = güçlü bloom)

    // Ok icon boyutu
    iconSize: "w-5 h-5",
    buttonSize: "w-12 h-12",

    // Hover'da ok ikonunun kayma animasyonu
    iconShift: 4,   // piksel — sola/sağa kayma miktarı
  },

  // ─────────────────────────────────────────────
  //  🌈  HOVER RENK EFEKTI (kart üzerine gelinince)
  // ─────────────────────────────────────────────
  cardHover: {
    overlayOpacity: 0.3,   // Renk katmanı opaklığı: 0.1 (hafif) → 0.5 (yoğun)
    imageScale: 1.08,  // Görsel zoom: 1.0 (zoom yok) → 1.15 (belirgin zoom)
    imageBrightness: 1.1,   // Parlaklık artışı: 1.0 (sabit) → 1.2 (çok parlak)

    // NEON ARKAPLAN GLOW
    // Projenin color değerini kullanarak kartın arkasını yumuşakça aydınlatır
    // glowOpacity: 0.0 (yok) → 0.15 (soft) → 0.4 (belirgin)
    glowOpacity: 0.12,
    // glowSpread: "200px" = dar odak | "500px" = geniş yayılan atmosfer
    glowSpread: "320px",
    // glowDuration: geçiş süresi (saniye)
    glowDuration: 0.6,
  },

};

// ====================================================================
//   BURADAN AŞAĞI KOD — DEĞİŞTİRMEN GEREKMİYOR
// ====================================================================

// ─── Su dolma efekti başlık ─────────────────────────────────────────
// clip-path ile altta başlayan beyaz dolgu yukarı çıkar — "su doluyormuş" hissi
const LiquidFillText = ({ text, delay = 0, className = '', fillColor, outlineColor, fillDuration, fillDelay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  fillColor = fillColor ?? PROJECTS_SETTINGS.heading.fillColor;
  outlineColor = outlineColor ?? PROJECTS_SETTINGS.heading.outlineColor;
  fillDuration = fillDuration ?? PROJECTS_SETTINGS.heading.fillDuration;
  fillDelay = fillDelay ?? PROJECTS_SETTINGS.heading.fillDelay;

  return (
    <div ref={ref} className={`relative inline-block overflow-hidden ${className}`}>
      {/* Katman 1: kontur / boş metin */}
      <h2
        className="text-3xl md:text-5xl lg:text-6xl font-display font-bold select-none"
        style={{ color: outlineColor, WebkitTextStroke: `1px ${outlineColor}` }}
      >
        {text}
      </h2>

      {/* Katman 2: dolu metin — clip-path ile aşağıdan yukarı açılır */}
      <motion.h2
        className="absolute inset-0 text-3xl md:text-5xl lg:text-6xl font-display font-bold"
        style={{ color: fillColor }}
        initial={{ clipPath: 'inset(100% 0% 0% 0%)' }}
        animate={isInView
          ? { clipPath: 'inset(0% 0% 0% 0%)' }
          : { clipPath: 'inset(100% 0% 0% 0%)' }
        }
        transition={{
          duration: fillDuration,
          delay: delay + fillDelay,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        {text}
      </motion.h2>
    </div>
  );
};


// ─── Neon ok butonu ─────────────────────────────────────────────────
const NeonArrowButton = ({ direction, onClick, disabled, colors, glowLayers }) => {
  const [hovered, setHovered] = useState(false);
  const [neonColor, setNeonColor] = useState('#00f0ff');
  const { buttonSize, iconSize, iconShift } = PROJECTS_SETTINGS.arrows;
  colors = colors ?? PROJECTS_SETTINGS.arrows.colors;
  glowLayers = glowLayers ?? PROJECTS_SETTINGS.arrows.glowLayers;

  const handleEnter = useCallback(() => {
    const c = colors[Math.floor(Math.random() * colors.length)];
    setNeonColor(c);
    setHovered(true);
  }, [colors]);

  const buildGlow = (color) => {
    const layers = ['0 0 8px', '0 0 20px', '0 0 40px'];
    return layers.slice(0, glowLayers).map(l => `${l} ${color}`).join(', ');
  };

  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setHovered(false)}
      className={`${buttonSize} border flex items-center justify-center transition-all duration-300 relative overflow-hidden z-20
        ${disabled ? 'opacity-30 cursor-not-allowed border-black/30 dark:border-white/20' : 'cursor-pointer'}`}
      style={hovered && !disabled ? {
        borderColor: neonColor,
        boxShadow: buildGlow(neonColor),
        background: 'rgba(255,255,255,0.04)',
      } : {
        borderColor: 'rgba(255,255,255,0.2)',
      }}
      animate={hovered && !disabled ? { scale: 1.05 } : { scale: 1 }}
      transition={{ duration: 0.15 }}
    >
      {/* Neon glow halo arkasında */}
      {hovered && !disabled && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.4, 0.7, 0.4] }}
          transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
          style={{ background: `radial-gradient(circle, ${neonColor}22 0%, transparent 70%)` }}
        />
      )}

      <motion.div
        animate={hovered && !disabled
          ? { x: direction === 'right' ? iconShift : -iconShift }
          : { x: 0 }
        }
        transition={{ duration: 0.2 }}
      >
        <Icon
          className={iconSize}
          style={{
            color: hovered && !disabled ? neonColor : 'white',
            filter: hovered && !disabled ? `drop-shadow(0 0 6px ${neonColor})` : 'none',
            transition: 'color 0.2s, filter 0.2s'
          }}
        />
      </motion.div>
    </motion.button>
  );
};


// ─── TextReveal ─────────────────────────────────────────────────────
const TextReveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: '100%' }}
        animate={isInView ? { y: 0 } : { y: '100%' }}
        transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
};


// ─── Ana bileşen ─────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export const ProjectsSection = () => {
  const ref = useRef(null);
  const sliderRef = useRef(null);   // slider container ref
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredProject, setHoveredProject] = useState(null);
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);

  const siteSettings = useSettings();
  // Projeler
  useEffect(() => {
    fetch(`${API}/projects`)
      .then(r => r.json())
      .then(data => setProjects(Array.isArray(data) ? data : []))
      .catch(() => { setProjects(PROJECTS_SETTINGS.projects); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Site ayarları (başlıklar, kart oranı vb.)


  const { layout, heading, arrows, cardHover } = PROJECTS_SETTINGS;

  // API'den gelen ayarlar yoksa PROJECTS_SETTINGS değerleri kullanılır
  // Mobil tespiti ve efektif görünür sayı
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const visibleCount = isMobile ? 1 : (siteSettings?.projects_visible ?? layout.visibleCount);
  const cardGap = siteSettings?.projects_card_gap ?? layout.cardGap;
  const cardAspect = siteSettings?.projects_card_aspect ?? layout.cardAspect;
  const headingLine1 = siteSettings?.projects_line1 ?? heading.line1;
  const headingLine2 = siteSettings?.projects_line2 ?? heading.line2;
  const fillColor = siteSettings?.projects_fill_color ?? heading.fillColor;
  const outlineColor = siteSettings?.projects_outline_color ?? heading.outlineColor;
  const fillDuration = siteSettings?.projects_fill_duration ?? heading.fillDuration;
  const fillDelay = siteSettings?.projects_fill_delay ?? heading.fillDelay;
  const arrowColors = siteSettings?.projects_arrow_colors ?? arrows.colors;
  const arrowGlowLayers = siteSettings?.projects_arrow_glow ?? arrows.glowLayers;
  const overlayOpacity = siteSettings?.projects_overlay_opacity ?? cardHover.overlayOpacity;
  const imageScale = siteSettings?.projects_image_scale ?? cardHover.imageScale;
  const imageBrightness = siteSettings?.projects_image_brightness ?? cardHover.imageBrightness;
  const glowOpacity = siteSettings?.projects_glow_opacity ?? cardHover.glowOpacity;
  const glowSpread = siteSettings?.projects_glow_spread ?? cardHover.glowSpread;
  const glowDuration = siteSettings?.projects_glow_duration ?? cardHover.glowDuration;

  // GridScan Ayarları
  const gsVisible = siteSettings?.gridscan_visible ?? true;
  const gsLinesColor = siteSettings?.gridscan_lines_color ?? "rgba(255,255,255,0.05)";
  const gsScanColor = siteSettings?.gridscan_scan_color ?? "rgba(255,255,255,0.15)";
  const gsGridScale = siteSettings?.gridscan_grid_scale ?? 0.15;
  const gsOpacity = siteSettings?.gridscan_opacity ?? 0.4;
  const gsScanOpacity = siteSettings?.gridscan_scan_opacity ?? 0.2;
  const gsLineThickness = siteSettings?.gridscan_line_thickness ?? 0.8;
  const gsScanDuration = siteSettings?.gridscan_scan_duration ?? 4;
  const gsScanDelay = siteSettings?.gridscan_scan_delay ?? 3;
  const gsScanDirection = siteSettings?.gridscan_scan_direction ?? 'pingpong';
  const gsBloomIntensity = siteSettings?.gridscan_bloom_intensity ?? 0;
  const gsNoiseIntensity = siteSettings?.gridscan_noise_intensity ?? 0.01;
  const gsLineStyle = siteSettings?.gridscan_line_style ?? 'solid';

  const maxIndex = Math.max(0, projects.length - visibleCount);


  const nextSlide = () => setCurrentIndex(i => Math.min(i + 1, maxIndex));
  const prevSlide = () => setCurrentIndex(i => Math.max(i - 1, 0));

  return (
    <section
      id="projects"
      ref={ref}
      data-testid="projects-section"
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-24 bg-black overflow-hidden"
    >
      {/* Background Grid Scan */}
      {gsVisible && (
        <div className="absolute inset-0 pointer-events-none z-0" style={{ opacity: gsOpacity }}>
          <GridScan
            linesColor={gsLinesColor}
            scanColor={gsScanColor}
            gridScale={gsGridScale}
            scanOpacity={gsScanOpacity}
            scanDuration={gsScanDuration}
            scanDelay={gsScanDelay}
            lineThickness={gsLineThickness}
            scanDirection={gsScanDirection}
            bloomIntensity={gsBloomIntensity}
            noiseIntensity={gsNoiseIntensity}
            lineStyle={gsLineStyle}
          />
        </div>
      )}

      <div className="max-w-screen-2xl mx-auto relative z-10">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <motion.span
              className="inline-block font-mono text-[10px] uppercase tracking-[0.3em] text-black/50 dark:text-white/30 border border-black/20 dark:border-white/10 px-4 py-2 mb-8"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            >
              {t('nav', 'projects')}
            </motion.span>

            {/* Su dolma efektli başlık */}
            <div className="space-y-1">
              <LiquidFillText text={headingLine1} delay={0} className="block" fillColor={fillColor} outlineColor={outlineColor} fillDuration={fillDuration} fillDelay={fillDelay} />
              <LiquidFillText text={headingLine2} delay={0.15} className="block" fillColor={fillColor} outlineColor={outlineColor} fillDuration={fillDuration} fillDelay={fillDelay} />
            </div>
          </div>

          {/* Neon oklar */}
          <motion.div
            className="flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: 0.3 }}
          >
            <NeonArrowButton
              direction="left"
              onClick={prevSlide}
              disabled={currentIndex === 0}
              colors={arrowColors}
              glowLayers={arrowGlowLayers}
            />
            <NeonArrowButton
              direction="right"
              onClick={nextSlide}
              disabled={currentIndex === maxIndex}
              colors={arrowColors}
              glowLayers={arrowGlowLayers}
            />
          </motion.div>
        </div>

        {/* Slider */}
        <div
          className="overflow-hidden"
          onTouchStart={e => { sliderRef.current._touchX = e.touches[0].clientX; }}
          onTouchEnd={e => {
            const dx = e.changedTouches[0].clientX - (sliderRef.current._touchX ?? 0);
            if (dx < -50) nextSlide();
            else if (dx > 50) prevSlide();
          }}
        >
          <motion.div
            ref={sliderRef}
            className={`flex ${isMobile ? 'gap-0' : cardGap}`}
            animate={{ x: isMobile ? `-${currentIndex * 100}%` : `-${currentIndex * (100 / visibleCount)}%` }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            {projects.map((project, index) => {
              const isHov = hoveredProject === project.id;
              // cardHover değerleri artık props'tan geliyor — üst scope'taki değişkenler kullanılır
              return (
                <div key={project.id} data-card className="flex-shrink-0 w-full md:w-[calc(33.333%-1rem)] relative">

                  {/* Neon glow — kartın arkasında, wrapper'a göre absolute */}
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      inset: '-40px',
                      background: `radial-gradient(ellipse at 50% 55%, ${project.color}60 0%, ${project.color}28 40%, transparent 70%)`,
                      opacity: isHov ? 1 : 0,
                      transition: `opacity ${glowDuration}s ease`,
                      filter: 'blur(50px)',
                      zIndex: 0,
                    }}
                  />

                  <motion.div
                    data-testid={`project-card-${project.id}`}
                    className="group relative"
                    style={{ zIndex: 1 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    onMouseEnter={() => setHoveredProject(project.id)}
                    onMouseLeave={() => setHoveredProject(null)}
                  >
                    <Link to={`/project/${project.id}`} className="block">

                      {/* Görsel */}
                      <div className={`relative ${cardAspect} overflow-hidden mb-4`}>
                        <motion.img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover"
                          animate={{
                            scale: hoveredProject === project.id
                              ? imageScale : 1,
                            filter: hoveredProject === project.id
                              ? `brightness(${imageBrightness})`
                              : 'brightness(0.9)',
                          }}
                          transition={{ duration: 0.5 }}
                        />

                        {/* Renk katmanı */}
                        <motion.div
                          className="absolute inset-0"
                          style={{ backgroundColor: project.color }}
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: hoveredProject === project.id
                              ? overlayOpacity : 0
                          }}
                        />

                        {/* Etiketler */}
                        <div className="absolute top-4 left-4 flex gap-2">
                          {project.tags.map((tag) => (
                            <motion.span
                              key={tag}
                              className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-white dark:bg-black/80 backdrop-blur-sm"
                              initial={{ y: -10, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              {tag}
                            </motion.span>
                          ))}
                        </div>

                        {/* View Project butonu */}
                        <AnimatePresence>
                          {hoveredProject === project.id && (
                            <motion.div
                              className="absolute inset-0 flex items-center justify-center"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              <motion.span
                                className="px-6 py-3 bg-white text-black font-mono text-xs uppercase tracking-wider flex items-center gap-2"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                              >
                                {t('projects', 'view_project', 'View Project')}
                                <ExternalLink className="w-4 h-4" />
                              </motion.span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Proje bilgisi */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg md:text-xl font-display font-bold mb-1 text-white transition-colors">
                            {project.title}
                          </h3>
                          <p className="text-sm text-white/40 font-body">
                            {project.author}
                          </p>
                        </div>
                        <motion.div
                          animate={{
                            x: hoveredProject === project.id ? 0 : -5,
                            opacity: hoveredProject === project.id ? 1 : 0.3,
                          }}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </motion.div>
                      </div>

                    </Link>
                  </motion.div>
                </div>
              );
            })}
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          className="mt-12 pt-8 border-t border-black/20 dark:border-white/10 flex flex-wrap items-center justify-between gap-4"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.6 }}
        >
          <span className="font-mono text-xs text-black/50 dark:text-white/30">
            {currentIndex + 1}–{Math.min(currentIndex + visibleCount, projects.length)} / {projects.length} projects
          </span>
          <Link
            to="/#contact"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-black/70 dark:text-white/50 hover:text-black dark:text-white transition-colors"
          >
            {t('projects', 'add_yours', 'Add your project to this list')}
            <motion.span whileHover={{ x: 4 }}>→</motion.span>
          </Link>
        </motion.div>

      </div>
    </section>
  );
};

export default ProjectsSection;